/**
 * Custom hook for virtual scrolling in wall preview
 * Only renders blocks that are visible in the current viewport
 */
import { useMemo } from 'react';
import { calculateVisibleBlocks, DEFAULT_VIRTUAL_SCROLL_CONFIG } from '../utils';
/**
 * Hook for virtual scrolling wall blocks
 * Returns only the blocks that should be rendered based on current scroll position
 */
export const useVirtualScroll = ({ segments, scrollPosition, containerWidth, containerHeight, config = DEFAULT_VIRTUAL_SCROLL_CONFIG, }) => {
    // Calculate total blocks for performance monitoring
    const totalBlocks = useMemo(() => {
        if (!segments || segments.length === 0)
            return 0;
        return segments.reduce((total, segment) => { var _a; return total + (((_a = segment.frontView) === null || _a === void 0 ? void 0 : _a.length) || 0); }, 0);
    }, [segments]);
    // Only enable virtual scrolling if we have many blocks
    const isEnabled = totalBlocks > 100; // Threshold for enabling virtualization
    // Calculate visible blocks
    const visibleBlocks = useMemo(() => {
        if (!isEnabled || !segments || segments.length === 0) {
            // If not enabled or no segments, return all blocks (fallback to original behavior)
            const allBlocks = [];
            segments === null || segments === void 0 ? void 0 : segments.forEach((segment, segmentIndex) => {
                var _a;
                (_a = segment.frontView) === null || _a === void 0 ? void 0 : _a.forEach((block, blockIndex) => {
                    var _a, _b;
                    allBlocks.push({
                        segmentIndex,
                        blockIndex,
                        x: block.x || 0,
                        y: block.y || 0,
                        width: ((_a = segment.preview) === null || _a === void 0 ? void 0 : _a.blockLength) || 50,
                        height: ((_b = segment.preview) === null || _b === void 0 ? void 0 : _b.blockHeight) || 50,
                    });
                });
            });
            return allBlocks;
        }
        // Use virtual scrolling calculation
        try {
            const visibilityInfo = calculateVisibleBlocks(segments, scrollPosition, containerWidth, containerHeight, config);
            return visibilityInfo.map((info) => ({
                segmentIndex: info.segmentIndex,
                blockIndex: info.blockIndex,
                x: info.x,
                y: info.y,
                width: info.width,
                height: info.height,
            }));
        }
        catch (error) {
            console.error('Error in virtual scroll calculation:', error);
            return [];
        }
    }, [segments, scrollPosition, containerWidth, containerHeight, config, isEnabled]);
    return {
        visibleBlocks,
        totalBlocks,
        visibleBlockCount: visibleBlocks.length,
        isEnabled,
    };
};
